import React, { useMemo, useRef, useState } from "react";
import JoditBaseEditor from "jodit-react";
import type { IJodit } from "jodit/esm/types/jodit";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

type Props = {
    html: string;
    onChange: (html: string) => void;
    onImageSelected: (image: PendingProductQuestionImage) => void;
};

export type PendingProductQuestionImage = {
    token: string;
    file: File;
    previewUrl: string;
};

const makeToken = (): string => {
    if (typeof crypto.randomUUID === `function`) {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export default function ProductQuestionBlockEditor({
    html,
    onChange,
    onImageSelected,
}: Props) {
    const editorRef = useRef<IJodit | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const savedRangeRef = useRef<Range | null>(null);
    const [imageError, setImageError] = useState<string | null>(null);

    const config = useMemo(
        () => ({
            readonly: false,
            placeholder: `Введите текст`,
            askBeforePasteFromWord: false,
            askBeforePasteHTML: false,
            removeButtons: [`image`, `file`, `video`],
            uploader: {
                insertImageAsBase64URI: false,
            },
            extraButtons: [
                {
                    name: `insertProductQuestionJpg`,
                    icon: `image`,
                    tooltip: `Вставить JPG с компьютера`,
                    exec: (editor: IJodit) => {
                        editorRef.current = editor;
                        savedRangeRef.current = editor.s.range.cloneRange();
                        fileInputRef.current?.click();
                    },
                },
            ],
        }),
        [],
    );

    const selectImage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        event.target.value = ``;

        if (!file) {
            return;
        }

        if (file.type !== `image/jpeg`) {
            setImageError(`Можно загружать только изображения JPG/JPEG.`);
            return;
        }

        if (file.size > MAX_IMAGE_SIZE) {
            setImageError(`Размер изображения не должен превышать 5 МБ.`);
            return;
        }

        const editor = editorRef.current;

        if (!editor) {
            return;
        }

        setImageError(null);

        const token = makeToken();
        const previewUrl = URL.createObjectURL(file);

        if (savedRangeRef.current) {
            editor.s.selectRange(savedRangeRef.current);
        }

        editor.s.insertHTML(
            `<img src="${previewUrl}" data-product-question-image="${token}" alt="">`,
        );
        editor.synchronizeValues();
        onImageSelected({ token, file, previewUrl });
        onChange(editor.value);
    };

    return (
        <>
            <JoditBaseEditor
                ref={editorRef}
                value={html}
                config={config}
                tabIndex={1}
                onBlur={onChange}
                onChange={onChange}
            />

            <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,image/jpeg"
                hidden
                onChange={selectImage}
            />

            {imageError && <div className="text-danger mt-2">{imageError}</div>}
        </>
    );
}
